
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";

/**
 * Checks if the user is registered as a master user
 */
export async function checkUserAccess(authUserId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('auth_user_id', authUserId)
      .eq('is_master', true)
      .maybeSingle();

    if (error) throw error;
    
    return !!data;
  } catch (error: any) {
    console.error("Error checking user access:", error.message);
    return false;
  }
}

/**
 * Checks if a user is registered (exists in the user_profiles table)
 */
export async function isUserRegistered(authUserId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (error) throw error;
    
    return !!data;
  } catch (error: any) {
    console.error("Error checking if user is registered:", error.message);
    return false;
  }
}

/**
 * Checks if a tenant already exists using database function
 */
export async function checkTenantExists(tenantId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('tenant_exists', { target_tenant_id: tenantId });

    if (error) throw error;
    
    return !!data;
  } catch (error: any) {
    console.error("Error checking if tenant exists:", error.message);
    return false;
  }
}

/**
 * Checks if a tenant already has a master user registered
 * Note: This is kept for backward compatibility but checkTenantExists
 * should be used instead due to RLS restrictions
 */
export async function checkTenantHasMasterUser(tenantId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')  // Updated from 'users' to 'user_profiles'
      .select('count')
      .eq('tenant_id', tenantId)
      .eq('is_master', true)
      .single();

    if (error) throw error;
    
    return data.count > 0;
  } catch (error: any) {
    console.error("Error checking if tenant has master user:", error.message);
    return false;
  }
}

/**
 * Registers a new tenant with the given company name
 */
export async function registerTenant(user: User, companyName: string): Promise<boolean> {
  try {
    if (!user) throw new Error("User must be logged in to register");
    
    const tenantId = user.app_metadata?.tenant_id;
    if (!tenantId) throw new Error("Missing tenant ID in user metadata");
    
    // Check if tenant already exists using our reliable database function
    const tenantExists = await checkTenantExists(tenantId);
    if (tenantExists) {
      throw new Error("This organization already has an administrator account. Please contact your organization administrator for access.");
    }
    
    const { data, error } = await supabase.functions.invoke("register-tenant", {
      body: {
        company_name: companyName,
        auth_id: user.id,
        tenant_id: tenantId
      }
    });

    if (error) throw error;
    
    if (!data.success) {
      throw new Error(data.error || "Failed to register tenant");
    }

    toast.success("Organization registered successfully");
    return true;
  } catch (error: any) {
    console.error("Error registering tenant:", error.message);
    toast.error(error.message);
    
    // If the error is due to tenant already having a master user, we want to redirect to the error page
    if (error.message.includes("organization already has an administrator") || 
        error.message.includes("Organization already registered")) {
      return false;
    }
    
    return false;
  }
}

/**
 * Signs in with Microsoft, optionally specifying if this is for signup
 */
export async function signInWithMicrosoft(redirectTo: string, redirectQueryParams?: Record<string, string>): Promise<void> {
  try {
   
    if (redirectQueryParams) {
      const queryString = new URLSearchParams(redirectQueryParams).toString();
      redirectTo += `?${queryString}`;
    }
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo,
        queryParams: {
          prompt: 'select_account',
        }
      }, 
  
    });

    if (error) throw error;
  } catch (error: any) {
    console.error("Error signing in with Microsoft:", error.message);
    toast.error(error.message);
    throw error;
  }
}

/**
 * Signs out the current user
 */
export async function signOut(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch (error: any) {
    console.error("Error signing out:", error.message);
    toast.error("Failed to sign out. Please try again.");
    throw error;
  }
}
